import { useState, useEffect } from "react";
import { Dumbbell, Play, StopCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const WORKOUT_SESSION_STORAGE_KEY = 'active_workout_session';

interface SavedSession {
  exercises: Array<{
    id: string;
    name: string;
    completed: boolean;
    skipped: boolean;
    actualSets?: Array<{ completed: boolean }>;
  }>;
  startTime: string;
  selectedDayIndex: number;
}

interface ResumeWorkoutBannerProps {
  onResume: () => void;
  onDiscard: () => void;
}

export function ResumeWorkoutBanner({ onResume, onDiscard }: ResumeWorkoutBannerProps) {
  const [savedSession, setSavedSession] = useState<SavedSession | null>(null);
  const [showEndConfirmation, setShowEndConfirmation] = useState(false);

  useEffect(() => {
    const checkForSession = () => {
      const stored = localStorage.getItem(WORKOUT_SESSION_STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as SavedSession;
          if (parsed.exercises?.length > 0) {
            setSavedSession(parsed);
          }
        } catch (e) {
          setSavedSession(null);
        }
      } else {
        setSavedSession(null);
      }
    };

    checkForSession();
    
    const handleStorage = () => checkForSession();
    window.addEventListener('storage', handleStorage);
    const interval = setInterval(checkForSession, 2000);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  if (!savedSession) return null;

  const totalSets = savedSession.exercises.reduce((acc, ex) => 
    acc + (ex.actualSets?.length || 0), 0
  );
  const completedSets = savedSession.exercises.reduce((acc, ex) => 
    acc + (ex.actualSets?.filter(s => s.completed).length || 0), 0
  );
  const progress = totalSets > 0 ? Math.round((completedSets / totalSets) * 100) : 0;

  const handleEndWorkout = () => {
    localStorage.removeItem(WORKOUT_SESSION_STORAGE_KEY);
    setSavedSession(null);
    setShowEndConfirmation(false);
    onDiscard();
  };

  return (
    <>
      <div className="px-4 pb-3 w-full">
        <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Dumbbell className="w-5 h-5 text-primary" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm mb-1">Workout In Progress</h3>
              
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                  <div 
                    className="h-full evolve-gradient transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground">{progress}%</span>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  className="flex-1 h-8 evolve-gradient"
                  onClick={onResume}
                >
                  <Play className="w-3 h-3 mr-1" />
                  Resume
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="h-8 border-destructive/50 text-destructive hover:bg-destructive/10"
                  onClick={() => setShowEndConfirmation(true)}
                >
                  <StopCircle className="w-3 h-3 mr-1" />
                  End
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={showEndConfirmation} onOpenChange={setShowEndConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End Workout?</AlertDialogTitle>
            <AlertDialogDescription>
              This will discard all progress from this workout session. Your logged sets and weights will not be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Workout</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEndWorkout}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              End Workout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function hasActiveWorkoutSession(): boolean {
  const stored = localStorage.getItem(WORKOUT_SESSION_STORAGE_KEY);
  if (!stored) return false;
  try {
    const parsed = JSON.parse(stored);
    return parsed.exercises?.length > 0;
  } catch {
    return false;
  }
}
